import { createContext, ReactNode, useMemo, useRef } from 'react';
import axios, { Axios } from 'axios';
import useAuth from '../hooks/useAuth';

interface ClientContextType {
  axiosClient: Axios;
}

export function createClientProvider(baseURL : string) {
  const ClientContext = createContext<ClientContextType | undefined>(undefined);
  function ClientProvider({ children }: { children: ReactNode }) {
    const { token, refreshAccessToken, checkTokenExpiry } = useAuth();

    const tokenRef = useRef(token);
  
    const axiosClient = useMemo(() => {
      const client = axios.create({
	baseURL,
	withCredentials: true
      });

      // refresh short-lived access token if necessary
      client.interceptors.request.use(async (config) => {
	const token = tokenRef.current;
	if (checkTokenExpiry(token)) {
	  const freshToken = await refreshAccessToken();
	  config.headers['Authorization'] = `Bearer ${freshToken}`;
	  tokenRef.current = freshToken;
	} else {
	  config.headers['Authorization'] = `Bearer ${token}`;
	}
	return config;
      }, (error) => {
	return Promise.reject(error);
      });
    
      return client;
    }, [tokenRef, refreshAccessToken, checkTokenExpiry]);

    return (
      <ClientContext.Provider value={{ axiosClient }}>
	{children}
      </ClientContext.Provider>
    );
  }

  return { ClientContext, ClientProvider };
}
